import { DateTime } from 'luxon';

import { IndexedBy } from '../utils';

// Shared Interfaces

interface LibraryBase {
  code: string;
  name: string;
  description: string;
}
interface LibraryBaseResponse extends LibraryBase {
  created: string;
  updated: string;
  lastPublished: string | null;
}
interface LibraryBaseParsed extends LibraryBase {
  created: DateTime;
  updated: DateTime;
  lastPublished: DateTime | null;
}

interface LibrarySnapshotBase {
  code: string;
}
interface LibrarySnapshotBaseResponse extends LibrarySnapshotBase {
  created: string;
}
interface LibrarySnapshotBaseParsed extends LibrarySnapshotBase {
  created: DateTime;
}

interface EntitySnapshotBase {
  /** The id of the entity being snapshotted (**not** the id of the snapshot itself). */
  id: string;
  /** The version of the entity being snapshotted (**not** the version of the snapshot itself). */
  version: number;
  /** The code of the entity being snapshotted (**not** the code of the snapshot itself). */
  code: string;
}

export type LibrarySnapshotStatus = 'PUBLISHED' | 'UNPUBLISHED';
export function isLibrarySnapshotStatus(statusString: string): statusString is LibrarySnapshotStatus {
  return statusString === 'PUBLISHED' || statusString === 'UNPUBLISHED';
}

export type EntityType = 'check' | 'schedule' | 'rule';
export function isEntityType(type: string): type is EntityType {
  return type === 'check' || type === 'schedule' || type === 'rule';
}

// Public Interfaces

export interface EntitySnapshotRaw extends EntitySnapshotBase {
  type: string;
}
export interface EntitySnapshot extends EntitySnapshotBase {
  type: EntityType;
}
/** An utility type for restricting an entity snapshot to having a particular entity type at compile-time. */
export interface EntitySnapshotWithType<TEntityType extends EntityType> extends EntitySnapshot {
  type: TEntityType;
}

/** A library response from the "get all" endpoint, before being parsed. */
export interface LibraryListingResponse extends LibraryBaseResponse {
  librarySnapshot: LibrarySnapshotBaseResponse & {
    status: string;
  };
}
/** A library as loaded from the "get all" endpoint. */
export interface LibraryListing extends LibraryBaseParsed {
  /** The library's latest snapshot, along with its publication status. */
  librarySnapshot: LibrarySnapshotBaseParsed & {
    /** Whether this snapshot has been published to any customers or not. */
    status: LibrarySnapshotStatus;
  };
}

/** A library response from the "get one" endpoint, before being parsed. */
export interface LibraryDetailedResponse extends LibraryBaseResponse {
  librarySnapshot: LibrarySnapshotBaseResponse & {
    entitySnapshots: EntitySnapshotRaw[];
  };
}
/** A library as loaded from the "get one" endpoint. */
export interface LibraryDetailed extends LibraryBaseParsed {
  /** The library's latest snapshot, including its contained entities. */
  librarySnapshot: LibrarySnapshotBaseParsed & {
    /** The entities snapshotted as part of this library snapshot. */
    entitySnapshots: EntitySnapshot[];
  };
}

/** The response from the library publication endpoint. */
export interface LibraryPublishResponse {
  /** The code of the library snapshot which was published. */
  librarySnapshot: string;
  /** The timestamp where this library snapshot was published. */
  published: string;
}

/** An interface representing a library to be saved, to be sent to the library creation/update endpoints. */
export interface LibraryDto {
  description: string;
  name: string;
  entitySnapshots: EntitySnapshotRaw[];
}

// Overall Reducer State

export type Library = LibraryListing | LibraryDetailed
  | (LibraryListing & LibraryDetailed); // merged type when both getall and getone endpoints called

export interface Libraries {
  allCodes: string[];
  /** The libraries which have been loaded by the app, indexed by their code. */
  byCode: IndexedBy<Library, 'code'>;
  /** The total number of libraries in this user's account (not just those we have fetched). */
  count: number | null;
  /** Libraries which have been modified by the user and should be sorted to the top of the current page. */
  recentlyModified: string[];
}
